// src/routes/RouteConfig.ts

import type {
  ComponentType,
} from 'react';

/**
 * ==============================
 * Coupon
 * ==============================
 */
import UserCouponPage
  from '../features/coupon/pages/UserCouponPage';

import AdminCouponPage
  from '../features/coupon/pages/AdminCouponPage';

/**
 * ==============================
 * Auth
 * ==============================
 */
import LoginPage
  from '../features/auth/login/pages/LoginPage';

import SignupPage
  from '../features/auth/signup/pages/SignupPage';

import FindIdPage
  from '../features/auth/find-id/pages/FindIdPage';

import ResetPasswordPage
  from '../features/auth/reset-password/pages/ResetPasswordPage';


/**
 * ==============================
 * Dashboard
 * ==============================
 */
import AdminDashboardPage
  from '../features/dashboard/pages/AdminDashboardPage';

import SuperAdminDashboardPage
  from '../features/dashboard/pages/SuperAdminDashboardPage';

import UserDashboardPage
  from '../features/dashboard/pages/UserDashboardPage';


/**
 * ==============================
 * Game
 * ==============================
 */
import GamesPage
  from '../features/game/pages/GamesPage';

import CreateGamePage
  from '../features/game/pages/CreateGamePage';

import GameDetailsPage
  from '../features/game/pages/GameDetailsPage';

import CreateTemplatePage
  from '../features/game/pages/CreateTemplatePage';

import EditTemplatePage
  from '../features/game/pages/EditTemplatePage';

import TVPage
  from '../features/game/pages/TVPage';


/**
 * ==============================
 * Member
 * ==============================
 */
import AdminMemberPage
  from '../features/member/pages/AdminMemberPage';

import AdminMemberDetailPage
  from '../features/member/pages/AdminMemberDetailPage';

import AdminPendingMemberPage
  from '../features/member/pages/AdminPendingMemberPage';

import UserProfilePage
  from '../features/member/pages/UserProfilePage';


/**
 * ==============================
 * Store
 * ==============================
 */
import SuperAdminStorePage
  from '../features/store/pages/SuperAdminStorePage';


/**
 * ==============================
 * Tournament
 * ==============================
 */
import TournamentDetailPage
  from '../features/tournament/pages/TournamentDetailPage';

import SuperAdminTournamentManagePage
  from '../features/tournament/pages/SuperadminTournamentManagePage';


/**
 * ==============================
 * System
 * ==============================
 */
import SuperAdminSystemManagePage
  from '../features/system/pages/SuperadminSystemManagePage';

import AdminSystemManagePage
  from '../features/system/pages/AdminSystemManagePage';


/**
 * ==============================
 * Poster
 * ==============================
 */
import SuperAdminPosterPage
  from '../features/poster/pages/SuperadminPosterPage';


/**
 * ==============================
 * Seed
 * ==============================
 */
import SeedHistoryPage
  from '../features/seed/pages/SeedHistoryPage';


/**
 * ==============================
 * Ranking
 * ==============================
 */
import Ranking
  from '../features/ranking/pages/Ranking';


/**
 * ==============================
 * Etc
 * ==============================
 */
import PreparingPage
  from '../features/etc/pages/PreparingPage';


/**
 * ==============================
 * Public
 * ==============================
 */
import LandingPage
  from '../pages/public/LandingPage';

import WithdrawalGuidePage
  from '../pages/public/WithdrawalGuidePage';


/**
 * ==============================
 * Role
 * ==============================
 */
export type UserRole =
  | 'USER'
  | 'ADMIN'
  | 'SUPERADMIN';


/**
 * ==============================
 * Route Types
 * ==============================
 */
export type RouteItem = {
  path: string;

  fullPath: string;

  title: string;

  roles:
  readonly UserRole[];

  component:
  ComponentType;
};


export type DynamicRouteItem =
  RouteItem & {
    to: (
      value:
        string | number,
    ) => string;
  };


export type RouteRoot = {
  path: string;

  fullPath: string;
};


/**
 * ==============================
 * Role Groups
 * ==============================
 */
const USER_ONLY = [
  'USER',
] as const;


const ADMIN_ONLY = [
  'ADMIN',
] as const;


const SUPERADMIN_ONLY = [
  'SUPERADMIN',
] as const;


const MANAGEMENT_ROLES = [
  'ADMIN',
  'SUPERADMIN',
] as const;


const ALL_AUTHENTICATED_ROLES = [
  'USER',
  'ADMIN',
  'SUPERADMIN',
] as const;


/**
 * ==============================
 * Route Config
 * ==============================
 */
const RouteConfig = {

  /**
   * ==============================
   * Public
   * ==============================
   */
  Public: {

    Landing: {
      path:
        '/',

      fullPath:
        '/',

      title:
        '홈페이지',

      roles: [],

      component:
        LandingPage,
    },


    WithdrawalGuide: {
      path:
        '/qna/withdrawal',

      fullPath:
        '/qna/withdrawal',

      title:
        '회원 탈퇴 안내',

      roles: [],

      component:
        WithdrawalGuidePage,
    },

  },


  /**
   * ==============================
   * Auth
   * ==============================
   */
  Auth: {

    Login: {
      path:
        '/login',

      fullPath:
        '/login',

      title:
        '로그인',

      roles: [],

      component:
        LoginPage,
    },


    Signup: {
      path:
        '/signup',

      fullPath:
        '/signup',

      title:
        '회원가입',

      roles: [],

      component:
        SignupPage,
    },


    FindId: {
      path:
        '/find-id',

      fullPath:
        '/find-id',

      title:
        '아이디 찾기',

      roles: [],

      component:
        FindIdPage,
    },

    ResetPassword: {
      path:
        '/reset-password',

      fullPath:
        '/reset-password',

      title:
        '비밀번호 찾기',

      roles: [],

      component:
        ResetPasswordPage,
    },

  },


  /**
   * ==============================
   * USER
   * ==============================
   */
  User: {

    Root: {
      path:
        '/user',

      fullPath:
        '/user',
    },


    Home: {
      path:
        'home',

      fullPath:
        '/user/home',

      title:
        '사용자 홈',

      roles:
        USER_ONLY,

      component:
        UserDashboardPage,
    },


    History: {
      path:
        'history',

      fullPath:
        '/user/history',

      title:
        '이용 내역',

      roles:
        USER_ONLY,

      component:
        SeedHistoryPage,
    },


    Tournaments: {
      path:
        'tournaments',

      fullPath:
        '/user/tournaments',

      title:
        '대회 일정',

      roles:
        USER_ONLY,

      component:
        TournamentDetailPage,
    },


    Ranking: {
      path:
        'ranking',

      fullPath:
        '/user/ranking',

      title:
        '랭킹',

      roles:
        USER_ONLY,

      component:
        Ranking,
    },


    Attendance: {
      path:
        'attendance',

      fullPath:
        '/user/attendance',

      title:
        '출석',

      roles:
        USER_ONLY,

      component:
        PreparingPage,
    },


    Coupons: {
      path:
        'coupons',

      fullPath:
        '/user/coupons',

      title:
        '쿠폰',

      roles:
        USER_ONLY,

      component:
        UserCouponPage,
    },


    Profile: {
      path:
        'profile',

      fullPath:
        '/user/profile',

      title:
        '프로필 관리',

      roles:
        USER_ONLY,

      component:
        UserProfilePage,
    },


    ComingSoon: {
      path:
        'coming-soon',

      fullPath:
        '/user/coming-soon',

      title:
        '준비 중',

      roles:
        USER_ONLY,

      component:
        PreparingPage,
    },

  },


  /**
   * ==============================
   * MANAGEMENT
   *
   * ADMIN 전용 기능 +
   * ADMIN / SUPERADMIN 공통 기능
   * ==============================
   */
  Management: {

    Root: {
      path:
        '/management',

      fullPath:
        '/management',
    },


    /**
     * ADMIN 전용 Dashboard
     *
     * /management/home
     */
    AdminHome: {
      path:
        'home',

      fullPath:
        '/management/home',

      title:
        '매장 관리자 홈',

      roles:
        ADMIN_ONLY,

      component:
        AdminDashboardPage,
    },


    /**
     * ==============================
     * Game
     *
     * ADMIN / SUPERADMIN
     * ==============================
     */
    Games: {
      path:
        'games',

      fullPath:
        '/management/games',

      title:
        '게임 관리',

      roles:
        MANAGEMENT_ROLES,

      component:
        GamesPage,
    },


    GameCreate: {
      path:
        'games/create',

      fullPath:
        '/management/games/create',

      title:
        '게임 생성',

      roles:
        MANAGEMENT_ROLES,

      component:
        CreateGamePage,
    },


    GameDetail: {
      path:
        'games/:playGameId',

      fullPath:
        '/management/games/:playGameId',

      title:
        '게임 상세',

      roles:
        MANAGEMENT_ROLES,

      component:
        GameDetailsPage,

      to: (
        playGameId:
          string | number,
      ) =>
        `/management/games/${playGameId}`,
    },


    TemplateCreate: {
      path:
        'games/templates/create',

      fullPath:
        '/management/games/templates/create',

      title:
        '템플릿 생성',

      roles:
        MANAGEMENT_ROLES,

      component:
        CreateTemplatePage,
    },


    TemplateEdit: {
      path:
        'games/templates/:id/edit',

      fullPath:
        '/management/games/templates/:id/edit',

      title:
        '템플릿 수정',

      roles:
        MANAGEMENT_ROLES,

      component:
        EditTemplatePage,

      to: (
        id:
          string | number,
      ) =>
        `/management/games/templates/${id}/edit`,
    },


    /**
     * ==============================
     * Member
     *
     * ADMIN / SUPERADMIN
     * ==============================
     */
    Members: {
      path:
        'members',

      fullPath:
        '/management/members',

      title:
        '회원 관리',

      roles:
        MANAGEMENT_ROLES,

      component:
        AdminMemberPage,
    },


    MemberDetail: {
      path:
        'members/:userId',

      fullPath:
        '/management/members/:userId',

      title:
        '회원 상세',

      roles:
        MANAGEMENT_ROLES,

      component:
        AdminMemberDetailPage,

      to: (
        userId:
          string | number,
      ) =>
        `/management/members/${userId}`,
    },


    WaitingMembers: {
      path:
        'waiting-members',

      fullPath:
        '/management/waiting-members',

      title:
        '가입 대기 회원',

      roles:
        MANAGEMENT_ROLES,

      component:
        AdminPendingMemberPage,
    },

    /**
     * ==============================
     * Coupon
     *
     * ADMIN / SUPERADMIN
     * ==============================
     */
    Coupons: {
      path:
        'coupons',

      fullPath:
        '/management/coupons',

      title:
        '쿠폰 관리',

      roles:
        MANAGEMENT_ROLES,

      component:
        AdminCouponPage,
    },


    /**
     * ==============================
     * TV
     *
     * ADMIN / SUPERADMIN
     * ==============================
     */
    TV: {
      path:
        'tv/:playGameId',

      fullPath:
        '/management/tv/:playGameId',

      title:
        'TV 화면',

      roles:
        MANAGEMENT_ROLES,

      component:
        TVPage,

      to: (
        playGameId:
          string | number,
      ) =>
        `/management/tv/${playGameId}`,
    },


    /**
     * ==============================
     * ADMIN 전용 매장 관리
     * ==============================
     */
    Setting: {
      path:
        'setting',

      fullPath:
        '/management/setting',

      title:
        '매장 관리',

      roles:
        ADMIN_ONLY,

      component:
        AdminSystemManagePage,
    },


    ComingSoon: {
      path:
        'coming-soon',

      fullPath:
        '/management/coming-soon',

      title:
        '준비 중',

      roles:
        MANAGEMENT_ROLES,

      component:
        PreparingPage,
    },

  },


  /**
   * ==============================
   * SUPERADMIN
   *
   * SUPERADMIN 전용 기능
   * ==============================
   */
  SuperAdmin: {

    Root: {
      path:
        '/superadmin',

      fullPath:
        '/superadmin',
    },


    /**
     * SUPERADMIN 전용 Dashboard
     */
    Home: {
      path:
        'home',

      fullPath:
        '/superadmin/home',

      title:
        '시스템 관리자 홈',

      roles:
        SUPERADMIN_ONLY,

      component:
        SuperAdminDashboardPage,
    },


    /**
     * 지점 관리
     */
    Stores: {
      path:
        'stores',

      fullPath:
        '/superadmin/stores',

      title:
        '지점 관리',

      roles:
        SUPERADMIN_ONLY,

      component:
        SuperAdminStorePage,
    },


    /**
     * 대회 관리
     */
    Tournaments: {
      path:
        'tournaments',

      fullPath:
        '/superadmin/tournaments',

      title:
        '대회 관리',

      roles:
        SUPERADMIN_ONLY,

      component:
        SuperAdminTournamentManagePage,
    },


    /**
     * ADMIN 계정 관리
     */
    Admins: {
      path:
        'admins',

      fullPath:
        '/superadmin/admins',

      title:
        '시스템 관리',

      roles:
        SUPERADMIN_ONLY,

      component:
        SuperAdminSystemManagePage,
    },


    /**
     * 포스터 관리
     */
    Posters: {
      path:
        'posters',

      fullPath:
        '/superadmin/posters',

      title:
        '포스터 관리',

      roles:
        SUPERADMIN_ONLY,

      component:
        SuperAdminPosterPage,
    },


    ComingSoon: {
      path:
        'coming-soon',

      fullPath:
        '/superadmin/coming-soon',

      title:
        '준비 중',

      roles:
        SUPERADMIN_ONLY,

      component:
        PreparingPage,
    },

  },


  /**
   * ==============================
   * Common
   * ==============================
   */
  Common: {

    ComingSoon: {
      path:
        '/coming-soon',

      fullPath:
        '/coming-soon',

      title:
        '준비 중',

      roles:
        ALL_AUTHENTICATED_ROLES,

      component:
        PreparingPage,
    },

  },

} as const;


export default RouteConfig;